import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { FiGrid, FiHome, FiPackage, FiShield, FiUser } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/authSession';
import { publicAsset } from '../../lib/publicPath';
import BrandLoader from './BrandLoader';

const SKIP_SPLASH_ONCE_KEY = 'sd_skip_native_splash_once';

function hasStoredAuthState() {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem('sd_auth') === 'true' || Boolean(localStorage.getItem('sd_auth_user'));
  } catch (_) {
    return false;
  }
}

export default function AppShell({ children }) {
  const isNative = Capacitor.isNativePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 900 : false
  );

  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const pullingRef = useRef(false);

  const pullReady = pullDistance > 84;
  const showPullRefresh = (isNative || isMobileViewport) && (pullDistance > 0 || refreshing);

  // Hide bottom menu on auth screens (login, signup, forgot-password, reset-password, verify-email)
  // Shows when user clicks Skip (navigates to /home) or after login
  const authRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
  const hideBottomMenu = authRoutes.includes(location.pathname);

  const hasActiveSession = isAuthenticated || Boolean(user) || hasStoredAuthState();

  // Solid, stable bottom menu without loading flicker
  const showBottomMenu = !hideBottomMenu && (isNative || isMobileViewport);

  const normalizedAvatarUrl = (() => {
    const avatarUrl = String(user?.avatarUrl || '').trim();
    if (!avatarUrl) return '';
    if (/^(https?:)?\/\//i.test(avatarUrl) || avatarUrl.startsWith('data:') || avatarUrl.startsWith('/')) {
      return avatarUrl;
    }
    return publicAsset(avatarUrl);
  })();

  const bottomMenuItems = useMemo(() => {
    if (hasActiveSession) {
      if (isAdmin) {
        return [
          { key: 'services', label: 'Services', icon: FiGrid, path: '/services' },
          { key: 'home', label: 'Home', icon: FiHome, path: '/home' },
          { key: 'admin', label: 'Admin', icon: FiShield, path: '/admin' },
          { key: 'account', label: 'Account', icon: FiUser, path: '/dashboard/profile' },
        ];
      }
      return [
        { key: 'services', label: 'Services', icon: FiGrid, path: '/services' },
        { key: 'home', label: 'Home', icon: FiHome, path: '/home' },
        { key: 'dashboard', label: 'Dashboard', icon: FiPackage, path: '/dashboard' },
        { key: 'account', label: 'Account', icon: FiUser, path: '/dashboard/profile' },
      ];
    }
    return [
      { key: 'services', label: 'Services', icon: FiGrid, path: '/services' },
      { key: 'home', label: 'Home', icon: FiHome, path: '/home' },
      { key: 'login', label: 'Login', icon: FiUser, path: '/login' },
      { key: 'signup', label: 'Signup', icon: FiPackage, path: '/signup' },
    ];
  }, [hasActiveSession, isAdmin]);

  const getIsActive = (item) => {
    const p = location.pathname;
    if (item.key === 'home') {
      return p === '/' || p === '/home';
    }
    if (item.key === 'services') {
      return p.startsWith('/services') || p.startsWith('/packages');
    }
    if (item.key === 'dashboard') {
      return p === '/dashboard' || (p.startsWith('/dashboard') && !p.startsWith('/dashboard/profile') && !p.startsWith('/dashboard/settings') && !p.startsWith('/dashboard/security'));
    }
    if (item.key === 'admin') {
      return p.startsWith('/admin');
    }
    if (item.key === 'account') {
      return p.startsWith('/dashboard/profile') || p.startsWith('/dashboard/settings') || p.startsWith('/dashboard/security');
    }
    if (item.key === 'login') {
      return p === '/login';
    }
    if (item.key === 'signup') {
      return p === '/signup';
    }
    return p === item.path;
  };

  const navLockRef = useRef(false);

  const handleBottomNavClick = (item) => {
    if (navLockRef.current) return;

    const isCurrent =
      (item.key === 'home' && (location.pathname === '/' || location.pathname === '/home'))
      || location.pathname === item.path;

    if (isCurrent) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    navLockRef.current = true;
    navigate(item.path);

    window.setTimeout(() => {
      navLockRef.current = false;
    }, 350);
  };

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const pullDistRef = useRef(0);

  useEffect(() => {
    // Enable pull down to refresh on both native mobile apps and mobile viewports
    if (!isNative && !isMobileViewport) return undefined;

    const onTouchStart = (event) => {
      if (window.scrollY > 0 || refreshing) return;
      if (document.body.classList.contains('mobile-nav-open') || document.body.classList.contains('cart-drawer-open')) return;
      const target = event.target;
      if (target?.closest('input, textarea, select, button, a, [role="button"]')) return;
      touchStartY.current = event.touches[0]?.clientY || 0;
      pullingRef.current = true;
    };

    const onTouchMove = (event) => {
      if (!pullingRef.current || refreshing) return;
      if (window.scrollY > 0) {
        if (pullDistRef.current !== 0) {
          pullDistRef.current = 0;
          setPullDistance(0);
        }
        pullingRef.current = false;
        return;
      }
      const currentY = event.touches[0]?.clientY || 0;
      const delta = currentY - touchStartY.current;
      if (delta <= 24) {
        if (pullDistRef.current !== 0) {
          pullDistRef.current = 0;
          setPullDistance(0);
        }
        return;
      }
      const dist = Math.min(100, (delta - 24) * 0.4);
      pullDistRef.current = dist;
      setPullDistance(dist);
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      const finalDist = pullDistRef.current;
      pullDistRef.current = 0;
      setPullDistance(0);

      if (finalDist >= 88) {
        setRefreshing(true);
        window.setTimeout(() => {
          try { localStorage.setItem(SKIP_SPLASH_ONCE_KEY, 'true'); } catch (_) {}
          window.location.reload();
        }, 240);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [isNative, isMobileViewport, refreshing]);

  return (
    <>
      <div className={`mobile-app-shell ${showBottomMenu ? 'has-bottom-user-menu' : ''}`}>
        {children}
      </div>
      {showBottomMenu ? (
        <nav className="mobile-bottom-user-menu" aria-label="Quick actions">
          {bottomMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = getIsActive(item);
            const showAvatar = item.key === 'account' && normalizedAvatarUrl !== '';

            return (
              <button
                key={item.key}
                type="button"
                className={`mobile-bottom-user-menu-item ${isActive ? 'active' : ''}`}
                onClick={() => handleBottomNavClick(item)}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <div className="mobile-bottom-user-menu-icon-wrap">
                  {showAvatar ? (
                    <img src={normalizedAvatarUrl} alt={item.label} className="mobile-bottom-user-menu-avatar" />
                  ) : (
                    <Icon aria-hidden="true" />
                  )}
                </div>
                <span className="mobile-bottom-user-menu-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
      ) : null}
      <div
        className={`pull-refresh-indicator ${showPullRefresh ? 'visible' : ''} ${pullReady ? 'ready' : ''}`}
        style={{ transform: `translate(-50%, ${Math.max(-120, -110 + pullDistance)}px)` }}
      >
        <BrandLoader compact label={refreshing ? 'Refreshing' : 'Pull to refresh'} />
      </div>
    </>
  );
}
